/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Bot,
  CandlestickChart,
  CheckCircle2,
  Gauge,
  History,
  LineChart,
  Loader2,
  Mail,
  Plug,
  SearchCheck,
  Settings2,
  ShieldCheck,
} from "lucide-react";
import PaxiaLogo from "../../branding/logo";
import homepageContent from "../../content/homepageContent";

const sectionClass = "landing-section px-4 py-14 sm:px-6 sm:py-16 lg:px-8 lg:py-20";

const navItems = [
  { label: "Workflow", href: "#workflow" },
  { label: "Workspace", href: "#workspace" },
  { label: "Automation", href: "#automation" },
  { label: "Trust", href: "#trust" },
];

const workflowCards = [
  {
    icon: Plug,
    title: "Connect accounts",
    body: "Bring supported broker accounts into one operating layer.",
  },
  {
    icon: ShieldCheck,
    title: "Set limits",
    body: "Define daily loss, risk per trade, exposure, size caps, and cooldowns before live action.",
  },
  {
    icon: CandlestickChart,
    title: "Trade from Studio",
    body: "Keep chart, account, symbol, sizing, positions, and notes in the same workspace.",
  },
  {
    icon: History,
    title: "Review, then automate",
    body: "Use history and journals to improve the process before automating repeatable work.",
  },
];

const workspaceCards = [
  {
    icon: Gauge,
    title: "Dashboard",
    body: "Monitor balances, equity, margin, positions, pending orders, and account readiness.",
  },
  {
    icon: LineChart,
    title: "Studio",
    body: "Plan and size trades from chart context with the account and risk math close by.",
  },
  {
    icon: SearchCheck,
    title: "Research",
    body: "Use market, macro, news, earnings, options, alerts, and analysis context where supported.",
  },
  {
    icon: History,
    title: "Review",
    body: "Close the loop with trade history, attribution, journaling, and performance context.",
  },
];

const automationCards = [
  {
    icon: SearchCheck,
    title: "Research workflows",
    body: "Run pre-market briefs, market scans, signal checks, and post-trade reviews around your process.",
  },
  {
    icon: Bot,
    title: "AI systems",
    body: "Describe your playbook, then shape the tools, data, conditions, schedules, and outputs.",
  },
  {
    icon: Settings2,
    title: "Execution modes",
    body: "Choose whether a workflow reports, alerts, drafts, requests review, or runs inside configured rules.",
  },
  {
    icon: Gauge,
    title: "Run history",
    body: "Track runs, cost, reliability, outcomes, and changes as the workflow evolves.",
  },
];

const trustPoints = [
  "Software platform, not financial advice",
  "Client funds remain with the connected broker",
  "Trading still involves the risk of loss",
  "Configured rules help govern new actions",
];

export default function StreamlinedHomepage({ content = homepageContent }) {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const utm = useMemo(() => {
    if (typeof window === "undefined") {
      return { source: "", medium: "", campaign: "", content: "" };
    }

    const params = new URLSearchParams(window.location.search);
    return {
      source: params.get("utm_source") || "",
      medium: params.get("utm_medium") || "",
      campaign: params.get("utm_campaign") || "",
      content: params.get("utm_content") || "homepage-streamlined",
    };
  }, []);

  const isEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  async function handleSubmit(event) {
    event.preventDefault();
    setError("");
    setMessage("");

    if (!isEmailValid) {
      setError("Please enter a valid email.");
      return;
    }

    setLoading(true);
    try {
      const response = await fetch("/api/waitlist", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, ...utm }),
      });

      if (!response.ok) {
        throw new Error("Submission failed. Try again.");
      }

      setMessage("Thanks - you're on the list.");
      setEmail("");
    } catch (submissionError) {
      setError(submissionError.message || "Submission failed. Try again.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <main className="landing-shell systems-homepage min-h-screen overflow-x-hidden">
      <StreamlinedHero content={content} />
      <WorkflowSection />
      <WorkspaceSection content={content} />
      <AutomationSection content={content} />
      <TrustSection />
      <SignupSection
        content={content}
        email={email}
        error={error}
        isEmailValid={isEmailValid}
        loading={loading}
        message={message}
        onEmailChange={setEmail}
        onSubmit={handleSubmit}
      />
      <Footer content={content} />
    </main>
  );
}

function StreamlinedHero({ content }) {
  const { brand, hero } = content;

  return (
    <header className="landing-hero">
      <nav className="relative z-10 mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
        <a href="#top" className="flex items-center gap-3" aria-label={`${brand.name} home`}>
          <img src={PaxiaLogo} alt={brand.name} className="h-12 w-auto sm:h-[3.75rem]" />
        </a>

        <div className="hidden items-center gap-7 text-sm text-slate-300 md:flex">
          {navItems.map((item) => (
            <a key={item.href} href={item.href} className="transition-colors hover:text-[#00D5FF]">
              {item.label}
            </a>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <Link
            to={hero.secondaryCta.href}
            className="hidden rounded-[8px] px-3 py-2 text-sm font-medium text-slate-300 transition-colors hover:bg-white/5 hover:text-white sm:inline-flex"
          >
            {hero.secondaryCta.label}
          </Link>
          <a
            href={hero.primaryCta.href}
            className="brand-button px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#00D5FF] focus:ring-offset-2 focus:ring-offset-[#05070D]"
          >
            {hero.primaryCta.label}
            <ArrowRight className="h-4 w-4" />
          </a>
        </div>
      </nav>

      <section id="top" className="relative z-10 mx-auto grid max-w-7xl gap-10 px-4 pb-14 pt-10 sm:px-6 sm:pb-16 sm:pt-14 lg:grid-cols-[0.92fr_1.08fr] lg:px-8 lg:pb-20 lg:pt-16">
        <div className="flex flex-col justify-center">
          <p className="landing-eyebrow mb-5 px-3 py-1 text-xs font-semibold uppercase">{hero.eyebrow}</p>
          <h1 className="brand-text-gradient max-w-4xl text-5xl font-semibold leading-[1.02] sm:text-6xl lg:text-7xl">
            {hero.title}
          </h1>
          <p className="mt-6 max-w-2xl text-lg leading-8 text-slate-300 sm:text-xl">
            Paxia.ai brings broker accounts, risk controls, research, Studio, review, and automation into one operating environment, so each trade stays tied to the account, the limits, and the evidence behind it.
          </p>

          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <a
              href={hero.primaryCta.href}
              className="brand-button px-5 py-3 text-base focus:outline-none focus:ring-2 focus:ring-[#00D5FF] focus:ring-offset-2 focus:ring-offset-[#05070D]"
            >
              {hero.primaryCta.label}
              <ArrowRight className="h-5 w-5" />
            </a>
            <Link
              to={hero.secondaryCta.href}
              className="secondary-button px-5 py-3 text-base focus:outline-none focus:ring-2 focus:ring-white/30 focus:ring-offset-2 focus:ring-offset-[#05070D]"
            >
              {hero.secondaryCta.label}
            </Link>
          </div>

          <ul className="mt-8 grid gap-2 text-sm text-slate-300 sm:grid-cols-2">
            {hero.trustLine.map((item) => (
              <li key={item} className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 flex-none text-[#00D5FF]" />
                {item}
              </li>
            ))}
          </ul>
        </div>

        <HeroPreview content={content} />
      </section>
    </header>
  );
}

function HeroPreview({ content }) {
  const { brand, hero } = content;

  return (
    <div className="relative self-center">
      <div className="soft-panel p-2 sm:p-3">
        <div className="overflow-hidden rounded-[8px] border border-white/10 bg-[#070B13]/90">
          <div className="h-1 bg-[linear-gradient(90deg,#0B3DFF,#00D5FF,#6D5BFF)]" />
          <div className="flex items-center justify-between border-b border-white/10 px-4 py-3">
            <div>
              <p className="text-xs uppercase text-slate-500">{brand.category}</p>
              <p className="mt-1 font-semibold text-white">{brand.name}</p>
            </div>
            <div className="status-pill rounded-[8px] border border-[#00D5FF]/25 bg-[#00D5FF]/10 px-3 py-1.5 text-xs font-semibold text-cyan-50">
              <span className="status-dot" />
              {hero.studioPreview.account.value}
            </div>
          </div>

          <div className="grid gap-3 p-4 lg:grid-cols-[0.9fr_1.1fr]">
            <div className="space-y-3">
              <PreviewPanel title={hero.studioPreview.account.label} icon={ShieldCheck}>
                <p className="text-lg font-semibold text-white">{hero.studioPreview.account.detail}</p>
                <p className="mt-2 text-sm text-slate-400">Rules are checked before new action reaches the broker.</p>
              </PreviewPanel>

              <PreviewPanel title="Guardrails" icon={ShieldCheck}>
                <div className="space-y-2">
                  {hero.studioPreview.risk.map((item) => (
                    <PreviewRow key={item.label} label={item.label} value={item.value} />
                  ))}
                </div>
              </PreviewPanel>
            </div>

            <div className="space-y-3">
              <PreviewPanel title="Chart-led trade" icon={LineChart}>
                <ChartMock />
                <div className="mt-3 grid grid-cols-3 gap-2 text-center text-xs">
                  <MiniMetric label="Risk" value="0.5%" />
                  <MiniMetric label="R:R" value="2.1" />
                  <MiniMetric label="Status" value="Review" />
                </div>
              </PreviewPanel>

              <PreviewPanel title="Review queue" icon={History}>
                <div className="space-y-2">
                  {hero.studioPreview.activity.map((item) => (
                    <PreviewRow key={item.label} label={item.label} value={item.value} />
                  ))}
                </div>
              </PreviewPanel>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function WorkflowSection() {
  return (
    <section id="workflow" className={`${sectionClass} landing-section-panel`}>
      <div className="mx-auto max-w-7xl">
        <SectionIntro
          label="How Paxia.ai works"
          title="Run the trading day in one clear sequence."
          body="Connect the account, define the limits, work the trade, check the context, review the result, and automate only the parts worth repeating."
        />

        <ol className="workflow-rail grid gap-3 md:grid-cols-2 lg:grid-cols-4">
          {workflowCards.map((step, index) => {
            const Icon = step.icon;
            return (
              <li key={step.title} className="workflow-stage p-5">
                <div className="mb-4 flex items-center justify-between gap-4">
                  <div className="icon-tile">
                    <Icon className="h-5 w-5" />
                  </div>
                  <span className="font-mono text-xs text-[#00D5FF]/60">{String(index + 1).padStart(2, "0")}</span>
                </div>
                <h3 className="text-lg font-semibold text-white">{step.title}</h3>
                <p className="mt-3 text-sm leading-6 text-slate-400">{step.body}</p>
              </li>
            );
          })}
        </ol>
      </div>
    </section>
  );
}

function WorkspaceSection({ content }) {
  const { riskControls } = content;

  return (
    <section id="workspace" className={`${sectionClass} landing-section-mesh`}>
      <div className="mx-auto grid max-w-7xl items-center gap-8 lg:grid-cols-[0.95fr_1.05fr]">
        <div>
          <SectionIntro
            align="left"
            label="Broker-connected workspace"
            title="Keep the account, chart, and decision in the same place."
            body="Dashboard shows account state. Studio keeps the trade ticket, risk math, positions, catalysts, and notes close enough to act without losing context."
          />
          <div className="grid gap-3 sm:grid-cols-2">
            {workspaceCards.map((card) => (
              <CapabilityCard key={card.title} {...card} />
            ))}
          </div>
        </div>

        <div className="rule-sheet p-5 sm:p-6">
          <div className="mb-5 flex items-center justify-between gap-4">
            <div>
              <p className="text-xs uppercase text-slate-500">Account guardrails</p>
              <h3 className="mt-1 text-xl font-semibold text-white">Limits before live action</h3>
            </div>
            <ShieldCheck className="h-7 w-7 text-[#00D5FF]" />
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            {riskControls.features.slice(0, 6).map((feature) => (
              <div key={feature} className="trust-point p-4 text-sm leading-6 text-slate-300">
                <CheckCircle2 className="mb-3 h-5 w-5 text-[#00D5FF]" />
                {feature}
              </div>
            ))}
          </div>

          <p className="safety-note mt-5 p-4 text-sm leading-6">{riskControls.safetyNote}</p>
        </div>
      </div>
    </section>
  );
}

function AutomationSection({ content }) {
  const { aiTools } = content;

  return (
    <section id="automation" className={`${sectionClass} landing-section-wash`}>
      <div className="mx-auto max-w-7xl">
        <div className="grid gap-8 lg:grid-cols-[0.9fr_1.1fr]">
          <div>
            <SectionIntro
              align="left"
              label="Research and automation"
              title="Build workflows around your playbook."
              body="Use Paxia.ai for research, monitoring, alerts, review, and automation without separating the process from account rules and trade evidence."
            />

            <div className="source-cloud flex flex-wrap gap-2.5">
              {aiTools.researchInputs.sources.slice(0, 8).map((source) => (
                <span key={source} className="source-tag px-3 py-1.5 text-xs font-medium text-slate-100">
                  {source}
                </span>
              ))}
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            {automationCards.map((card) => (
              <CapabilityCard key={card.title} {...card} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function TrustSection() {
  return (
    <section id="trust" className={sectionClass}>
      <div className="mx-auto max-w-7xl">
        <div className="grid gap-8 lg:grid-cols-[0.78fr_1.22fr]">
          <SectionIntro
            align="left"
            label="Trust and safety"
            title="Software for control, not a promise of returns."
            body="Paxia.ai is software for traders. It is not financial advice, it does not hold client funds, and it does not remove market risk."
          />
          <div className="grid gap-3 sm:grid-cols-2">
            {trustPoints.map((point) => (
              <div key={point} className="trust-point p-4 text-sm leading-6 text-slate-300">
                <CheckCircle2 className="mb-3 h-5 w-5 text-[#00D5FF]" />
                {point}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function SignupSection({ content, email, error, isEmailValid, loading, message, onEmailChange, onSubmit }) {
  const { finalCta } = content;

  return (
    <section id={finalCta.id} className="landing-section landing-cta px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
      <div className="mx-auto max-w-7xl">
        <div className="cta-stage p-6 sm:p-8 lg:p-10">
          <div className="grid gap-8 lg:grid-cols-[1fr_0.9fr]">
            <div className="relative z-10">
              <p className="mb-4 text-xs font-semibold uppercase tracking-[0.24em] text-[#8BE9FF]">Beta access</p>
              <h2 className="max-w-3xl text-4xl font-semibold leading-[1.04] text-white sm:text-5xl">
                If you already value process, Paxia.ai will make sense quickly.
              </h2>
              <p className="mt-5 max-w-2xl text-lg leading-8 text-slate-300">
                Join the beta if you want connected accounts, enforceable limits, integrated research, chart-led execution, review, and automation built around your own playbook.
              </p>
              <p className="mt-6 max-w-2xl text-sm leading-6 text-slate-400">{finalCta.microcopy}</p>
            </div>

            <div className="cta-form-shell p-5 sm:p-6" aria-live="polite">
              <div className="mb-5">
                <p className="text-sm font-semibold text-white">Request beta access</p>
                <p className="mt-2 text-sm leading-6 text-slate-400">Tell us where to reach you. We will use this only for beta access updates.</p>
              </div>

              {message ? (
                <div className="cta-success p-6 text-center" role="status">
                  <CheckCircle2 className="mx-auto h-10 w-10 text-[#00D5FF]" />
                  <p className="mt-3 text-lg font-semibold text-white">{message}</p>
                  <p className="mt-2 text-sm text-emerald-50">We received your submission and will be in touch.</p>
                </div>
              ) : (
                <form onSubmit={onSubmit} className="grid gap-3" aria-live="polite">
                  <label className="sr-only" htmlFor="streamlined-homepage-email">
                    Email
                  </label>
                  <input
                    id="streamlined-homepage-email"
                    type="email"
                    required
                    value={email}
                    onChange={(event) => onEmailChange(event.target.value)}
                    placeholder="you@domain.com"
                    className="min-h-12 rounded-[8px] border border-white/15 bg-[#05070D]/90 px-4 py-3 text-white outline-none transition-colors placeholder:text-slate-500 focus:border-[#00D5FF] focus:ring-2 focus:ring-[#00D5FF]/20"
                  />
                  <button
                    type="submit"
                    disabled={loading || !isEmailValid}
                    className="brand-button min-h-12 px-5 py-3 disabled:cursor-not-allowed disabled:bg-slate-600 disabled:text-slate-300 disabled:shadow-none"
                  >
                    {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Mail className="h-5 w-5" />}
                    {loading ? "Submitting..." : finalCta.primaryCta.label}
                  </button>
                </form>
              )}

              {error && <p className="mt-3 text-sm text-rose-200">{error}</p>}

              <div className="mt-5 flex flex-wrap items-center gap-3 text-sm text-slate-400">
                <span>No financial advice. No guaranteed returns.</span>
                <Link to={finalCta.secondaryCta.href} className="text-cyan-100 transition-colors hover:text-white">
                  {finalCta.secondaryCta.label}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Footer({ content }) {
  const { brand, footer } = content;

  return (
    <footer className="border-t border-white/10 bg-[#05070D] px-4 py-8 sm:px-6 lg:px-8">
      <div className="mx-auto flex max-w-7xl flex-col gap-6 text-sm text-slate-400 md:flex-row md:items-start md:justify-between">
        <div className="max-w-2xl">
          <img src={PaxiaLogo} alt={brand.name} className="h-10 w-auto" />
          <p className="mt-4 leading-6">{footer.disclaimer}</p>
          <p className="mt-3">&copy; {new Date().getFullYear()} {brand.name}. All rights reserved.</p>
        </div>
        <div className="flex flex-wrap gap-4 md:justify-end">
          {footer.links.map((link) =>
            link.href.startsWith("/") ? (
              <Link key={link.href} to={link.href} className="transition-colors hover:text-[#00D5FF]">
                {link.label}
              </Link>
            ) : (
              <a key={link.href} href={link.href} className="transition-colors hover:text-[#00D5FF]">
                {link.label}
              </a>
            ),
          )}
          <a href={`mailto:${footer.contactEmail}`} className="transition-colors hover:text-[#00D5FF]">
            {footer.contactEmail}
          </a>
        </div>
      </div>
    </footer>
  );
}

function SectionIntro({ label, title, body, align = "center" }) {
  const isCenter = align === "center";

  return (
    <div className={`${isCenter ? "mx-auto text-center" : ""} mb-10 max-w-3xl`}>
      <p className="mb-3 text-xs font-semibold uppercase text-[#00D5FF]">{label}</p>
      <h2 className="text-3xl font-semibold leading-tight text-white sm:text-4xl">{title}</h2>
      <p className="mt-4 text-base leading-7 text-slate-300">{body}</p>
      {isCenter && <div className="brand-divider mx-auto mt-6 max-w-sm" />}
    </div>
  );
}

function CapabilityCard({ icon: Icon, title, body }) {
  return (
    <article className="ai-capability p-5 sm:p-6">
      <div className="icon-tile mb-5 h-11 w-11 rounded-full">
        <Icon className="h-5 w-5" />
      </div>
      <h3 className="text-lg font-semibold text-white">{title}</h3>
      <p className="mt-3 text-sm leading-7 text-slate-300">{body}</p>
    </article>
  );
}

function PreviewPanel({ title, icon: Icon, children }) {
  return (
    <div className="landing-panel p-4">
      <div className="mb-4 flex items-center gap-2 text-sm font-semibold text-slate-300">
        <Icon className="h-4 w-4 text-[#00D5FF]" />
        {title}
      </div>
      {children}
    </div>
  );
}

function PreviewRow({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-[8px] border border-white/10 bg-white/[0.03] px-3 py-2">
      <span className="text-sm text-slate-400">{label}</span>
      <span className="text-right text-sm font-semibold text-cyan-100">{value}</span>
    </div>
  );
}

function MiniMetric({ label, value }) {
  return (
    <div className="metric-tile px-2 py-2">
      <div className="text-slate-500">{label}</div>
      <div className="mt-1 font-semibold text-white">{value}</div>
    </div>
  );
}

function ChartMock() {
  const bars = [34, 46, 38, 58, 42, 66, 52, 72, 60, 78, 64, 86];

  return (
    <div className="chart-surface h-40 p-4">
      <div className="flex h-full items-end gap-2">
        {bars.map((height, index) => (
          <div key={`${height}-${index}`} className="flex h-full flex-1 flex-col items-center justify-end gap-1">
            <div
              className={`chart-bar w-full rounded-sm ${index % 3 === 0 ? "bg-[#00D5FF]/80" : index % 3 === 1 ? "bg-[#0B3DFF]/80" : "bg-[#6D5BFF]/70"}`}
              style={{ height: `${height}%`, "--bar-delay": `${index * 90}ms` }}
            />
          </div>
        ))}
      </div>
    </div>
  );
}
